import { useState, useEffect, useContext, useRef } from "react";
import {
  Grid,
  Container,
  makeStyles,
  TableBody,
  TableHead,
  TablePagination,
  Chip,
  Typography,
} from "@material-ui/core";
import {
  Loader,
  SimpleToolbar,
  Toast,
  Drawer,
  StyledTableCell,
  StyledTableRow,
  BaseTable,
} from "../components";
import { doctorServices } from "../services";
import { AuthContext } from "../context";
import { useDrawer } from "../hooks";
import { capitalizeFirstLetterArray } from "../helpers";
import handleDownloadExcel from "../helpers/download-excel";

export default function DoctorsScreen(props) {
  const classes = useStyles();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(100);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const { authState, signOut } = useContext(AuthContext);
  const [toast, setToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const { isOpen, setIsOpen } = useDrawer();
  const tableHeader = useRef([
    "RUT",
    "Nombre",
    "Apellido",
    "Sexo",
    "Fecha de nacimiento",
    "Teléfono",
    "Teléfono de actividad",
    "Correo",
    "Especialidad 1",
    "Especialidad 2",
    "Especialidad 3",
    "Estatus",
  ]);
  const tableBody = useRef([]);
  async function getDoctors(text = "") {
    try {
      setLoading(true);
      const { status, response } = await doctorServices.getDoctorsFilter({
        text,
        page,
        limit,
        token: authState.token,
      });
      setLoading(false);
      if (status === 200) {
        setDoctors(response.doctors);
        setLimit(response.limit);
        setCount(response.total);
        setPage(response.page);
      } else if (status === 401) {
        signOut();
      }
    } catch (e) {
      console.log(e);
    }
  }
  useEffect(() => {
    const tableData = doctors.map((doctor) => {
      return {
        RUT: doctor.rut,
        Nombre: doctor.name || "",
        Apellido: doctor.lastName || "",
        Sexo: doctor.gender,
        "Fecha de nacimiento": doctor.birthdate,
        Teléfono: doctor.phone,
        "Teléfono de actividad": doctor.activityPhone,
        Correo: doctor.email,
        "Especialidad 1": doctor.specialties[0]
          ? doctor.specialties[0]?.name
          : "N/A",
        "Especialidad 2": doctor.specialties[1]
          ? doctor.specialties[1]?.name
          : "N/A",
        "Especialidad 3": doctor.specialties[2]
          ? doctor.specialties[2]?.name
          : "N/A",
        Estatus: doctor.userId ? "Activo" : "Inactivo",
      };
    });
    tableBody.current = tableData;
  }, [doctors]);

  useEffect(() => {
    getDoctors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, authState]);

  const exportDoctors = async () => {
    try {
      setLoading(true);
      const { status, response } = await doctorServices.exportDoctors({
        token: authState.user.token,
      });
      setLoading(false);
      if (status === 200) {
        var blob = new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        var link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = "usuarios.xlsx";
        link.click();
        window.URL.revokeObjectURL(link.href);
      } else if (status === 401) {
        signOut();
      }
    } catch (error) {
      setLoading(false);
      alert("Ha ocurrido un error.");
    }
  };

  const uploadDoctors = async (e) => {
    const doc = e.target.files[0];
    const formData = new FormData();
    formData.append("file", doc);
    try {
      setLoading(true);
      const { status, response } = await doctorServices.uploadDoctors({
        formData,
        token: authState.token,
      });
      setLoading(false);
      if (status === 200) {
        setToastMessage(response.message);
      } else if (status === 401) {
        signOut();
      } else {
        if (response.error) {
          setToastMessage(response.error.message);
        }
      }
      setToast(true);
    } catch (e) {
      console.log(e);
    }
  };

  const optionsToolbar = [
    {
      label: "Exportar excel",
      action: (e) => {
        handleDownloadExcel({
          filename: "Doctores",
          tableBody: tableBody.current,
          tableHeader: tableHeader.current,
        });
      },
    },
    {
      label: "Cargar medicos",
      action: (e) => {
        e.preventDefault();
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute(
          "style",
          " position: absolute; left: 0;   opacity: 0; "
        );
        input.setAttribute(
          "accept",
          ".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        );
        input.addEventListener("change", (e) => {
          uploadDoctors(e);
        });
        input.click();
      },
    },
    {
      label: "Exportar todos",
      action: (e) => {
        exportDoctors();
      },
    },
  ];
  return (
    <>
      <Drawer open={isOpen} setOpen={(value) => setIsOpen(value)} />
      <Grid container style={{ flex: 1 }}>
        {loading && <Loader loading={true} />}
        <Toast
          open={toast}
          message={toastMessage}
          close={() => setToast(false)}
        />
        <Grid item sm={12}>
          <div className={classes.root}>
            <Container maxWidth={false}>
              <SimpleToolbar
                hideExport={authState.user?.laboratory}
                onFilter={(text) => getDoctors(text)}
                setIsOpen={(value) => setIsOpen(value)}
                isOpen={isOpen}
                options={optionsToolbar}
              />
              <BaseTable tableId="tableDoctors">
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell>Rut</StyledTableCell>
                    <StyledTableCell>Nombre</StyledTableCell>
                    <StyledTableCell>Apellido</StyledTableCell>
                    <StyledTableCell>Sexo</StyledTableCell>
                    <StyledTableCell>Fecha de nacimiento</StyledTableCell>
                    {!authState.user?.laboratory && (
                      <>
                        <StyledTableCell>Teléfono</StyledTableCell>
                        <StyledTableCell>Teléfono de actividad</StyledTableCell>
                        <StyledTableCell>Correo</StyledTableCell>
                      </>
                    )}
                    <StyledTableCell>Especialidades</StyledTableCell>
                    {/* <StyledTableCell>Especialidad 1</StyledTableCell>
                    <StyledTableCell>Especialidad 2</StyledTableCell>
                    <StyledTableCell>Especialidad 3</StyledTableCell> */}
                    <StyledTableCell>Estatus</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {doctors.map((doctor, index) => {
                    const rut =
                      doctor.rut.charAt(doctor.rut.length - 2) === "-"
                        ? doctor.rut
                        : `${doctor.rut.slice(
                            0,
                            doctor.rut.length - 1
                          )}-${doctor.rut.slice(doctor.rut.length - 1)}`;
                    return (
                      <StyledTableRow hover key={doctor._id}>
                        <StyledTableCell style={{ textTransform: "uppercase" }}>
                          {rut}
                        </StyledTableCell>
                        <StyledTableCell>
                          {capitalizeFirstLetterArray(doctor.name)}
                        </StyledTableCell>
                        <StyledTableCell>
                          {capitalizeFirstLetterArray(doctor.lastName)}
                        </StyledTableCell>
                        {/* <StyledTableCell>{doctor.gender}</StyledTableCell> */}
                        <StyledTableCell>
                          <Typography
                            component={"span"}
                            data-label={doctor.gender}
                            className={classes.gender}
                          >
                            {doctor.gender.slice(0, 1)}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          {doctor.birthdate !== "Invalid date"
                            ? doctor.birthdate
                            : "N/A"}
                        </StyledTableCell>
                        {!authState.user.laboratory && (
                          <>
                            <StyledTableCell>
                              {doctor.phone ? doctor.phone : "N/A"}
                            </StyledTableCell>
                            <StyledTableCell>
                              {doctor.activityPhone
                                ? doctor.activityPhone
                                : "N/A"}
                            </StyledTableCell>
                            <StyledTableCell>
                              {doctor.email ? doctor.email : "N/A"}
                            </StyledTableCell>
                          </>
                        )}
                        {/* <StyledTableCell>
                        {doctor.specialties[0].name}
                      </StyledTableCell>
                      <StyledTableCell>
                        {doctor.specialties[1]
                          ? doctor.specialties[1].name
                          : "N/A"}
                      </StyledTableCell>
                      <StyledTableCell>
                        {doctor.specialties[2]
                          ? doctor.specialties[2].name
                          : "N/A"}
                      </StyledTableCell> */}
                        <StyledTableCell>
                          {doctor.specialties.map((spe, index) => (
                            <Chip
                              size="small"
                              variant="outlined"
                              color="default"
                              label={spe.name}
                              key={`${doctor._id}-${spe._id}-i${index}`}
                            />
                          ))}
                        </StyledTableCell>
                        <StyledTableCell>
                          {doctor.userId ? "Activo" : "Inactivo"}
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </BaseTable>
              <TablePagination
                labelRowsPerPage="Registros por página"
                component="div"
                count={count}
                onRowsPerPageChange={(e) => {
                  setLimit(e.target.value);
                }}
                onPageChange={(e, page) => {
                  setPage(page + 1);
                }}
                page={page - 1}
                rowsPerPage={limit}
                rowsPerPageOptions={[10, 25, 50, 100]}
                classes={{ root: classes.pagination }}
              />
            </Container>
          </div>
        </Grid>
      </Grid>
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  pagination: {
    // color: theme.palette.secondary.main,
    [theme.breakpoints.down("xs")]: {
      // position: "absolute",
      bottom: 0,
    },
  },
  gender: {
    borderRadius: 4,
    width: "30%",
    display: "inline-flex",
    justifyContent: "center",
    padding: 1,
    minWidth: 16,
    '&[data-label="FEMENINO"]': {
      background: "#ffcef4",
    },
    '&[data-label="MASCULINO"]': {
      background: "#cee7ff",
    },
  },
}));
